/**
 * Normalize AWS Rekognition DetectFaces + DetectLabels JSON into boxes.
 */
import { BOOL_FACE_ATTRS, FACE } from './helpers';

const fmtPct = (n) => (Number.isFinite(n) ? `${n.toFixed(1)}%` : null);
const boolVal = (attr) => (attr && typeof attr.Value === 'boolean' ? attr.Value : null);
const toCamel = (name) => name.charAt(0).toLowerCase() + name.slice(1);

const faceDetails = (face) => {
  const rows = [];
  const conf = fmtPct(face.Confidence);
  if (conf) rows.push(['Confidence', conf]);
  if (Number.isFinite(face.AgeRange?.Low) && Number.isFinite(face.AgeRange?.High)) {
    rows.push(['Age', `${face.AgeRange.Low} – ${face.AgeRange.High}`]);
  }
  if (face.Gender?.Value) {
    const g = fmtPct(face.Gender.Confidence);
    rows.push(['Gender', g ? `${face.Gender.Value} (${g})` : face.Gender.Value]);
  }
  BOOL_FACE_ATTRS.forEach((key) => {
    const val = face[key];
    if (!val || typeof val !== 'object' || !('Value' in val)) return;
    const c = fmtPct(val.Confidence);
    rows.push([key, c ? `${val.Value} (${c})` : String(val.Value)]);
  });
  if (face.Emotions?.length) {
    const top = [...face.Emotions]
      .filter((e) => e?.Type)
      .sort((a, b) => (b.Confidence || 0) - (a.Confidence || 0))
      .slice(0, 3)
      .map((e) => `${e.Type} ${fmtPct(e.Confidence) || ''}`.trim())
      .join(', ');
    if (top) rows.push(['Emotions', top]);
  }
  return rows;
};

const faceAttrFields = (face) => {
  const out = {};
  BOOL_FACE_ATTRS.forEach((key) => {
    out[toCamel(key)] = boolVal(face[key]);
  });
  return out;
};

/**
 * Normalize DetectFaces + DetectLabels JSON into one box list + scene label rows.
 * Face boxes carry demographics; Person (and other) boxes come only from Labels.
 */
const parseRekognition = (key, faces, labelsPayload) => {
  const boxes = [];
  (faces?.FaceDetails || []).forEach((face, i) => {
    if (!face?.BoundingBox) return;
    const emotions = [...(face.Emotions || [])]
      .filter((e) => e?.Type)
      .sort((a, b) => (b.Confidence || 0) - (a.Confidence || 0));
    boxes.push({
      id: `${key}-face-${i}`,
      label: FACE,
      confidence: Number(face.Confidence) || 0,
      ageLow: Number.isFinite(face.AgeRange?.Low) ? face.AgeRange.Low : null,
      ageHigh: Number.isFinite(face.AgeRange?.High) ? face.AgeRange.High : null,
      gender: face.Gender?.Value || null,
      genderConfidence: Number(face.Gender?.Confidence) || null,
      ...faceAttrFields(face),
      topEmotion: emotions[0]?.Type || null,
      topEmotionConfidence: Number(emotions[0]?.Confidence) || null,
      BoundingBox: face.BoundingBox,
      details: faceDetails(face),
      raw: face,
    });
  });

  const labels = [];
  (labelsPayload?.Labels || []).forEach((label) => {
    if (!label?.Name) return;
    const instances = (label.Instances || []).filter((inst) => inst?.BoundingBox);
    const parents = (label.Parents || []).map((p) => p.Name).filter(Boolean);
    const aliases = (label.Aliases || []).map((a) => a.Name).filter(Boolean);
    const categories = (label.Categories || []).map((c) => c.Name).filter(Boolean);
    labels.push({
      name: label.Name,
      confidence: Number(label.Confidence) || 0,
      parents,
      aliases,
      categories,
      instanceCount: instances.length,
      tileKey: key,
      raw: label,
    });
    instances.forEach((inst, i) => {
      const confidence = Number(inst.Confidence ?? label.Confidence) || 0;
      const details = [
        ['Confidence', fmtPct(confidence)],
        parents.length ? ['Parents', parents.join(', ')] : null,
        aliases.length ? ['Aliases', aliases.join(', ')] : null,
        categories.length ? ['Categories', categories.join(', ')] : null,
        Number.isFinite(label.Confidence) ? ['Label confidence', fmtPct(label.Confidence)] : null,
      ].filter(Boolean);
      boxes.push({
        id: `${key}-${label.Name}-${i}`,
        label: label.Name,
        confidence,
        parents,
        aliases,
        categories,
        BoundingBox: inst.BoundingBox,
        details,
        raw: {
          Name: label.Name,
          Confidence: label.Confidence,
          Parents: label.Parents || [],
          Aliases: label.Aliases || [],
          Categories: label.Categories || [],
          Instance: inst,
        },
      });
    });
  });

  return { boxes, labels };
};

export default parseRekognition;
export { parseRekognition };
